import { Box, Flex, Grid, Switch, useToast, VStack } from "@chakra-ui/react";
import { ApiMessageStack } from "components";
import {
  DEFAULT_ERROR_TOAST_OPTIONS,
  DEFAULT_SUCCESS_TOAST_OPTIONS,
} from "constants/default-toast-options";
import { AccountIntegrationTypeEnum } from "enums/account-integration-type.enum";
import { observer } from "mobx-react";
import React, { ChangeEvent, FC, useCallback } from "react";
import { AccountIntegrationsStore } from "store/UserAccounts/UserAccount/AccountIntegrations/AccountIntegrations.store";
import {
  TAccountIntegrationData,
  TAutomatedReferralsConfig,
} from "types/account-integration.type";

interface IProps {
  accountIntegrationsStore: AccountIntegrationsStore;
}

export const AutomatedReferralsSendOutOfAreaLeadsConfigurator: FC<IProps> = observer(
  ({ accountIntegrationsStore }) => {
    const toast = useToast();

    const accountIntegration = accountIntegrationsStore
      .accountIntegrationsMapByType[
      AccountIntegrationTypeEnum.AUTOMATED_REFERRALS
    ] as TAccountIntegrationData<TAutomatedReferralsConfig>;

    const integrationConfig = accountIntegration.integrationConfig;
    const sendOutOfAreaLeads = integrationConfig.sendOutOfAreaLeads ?? true;

    const handleSendOutOfAreaLeadsChange = useCallback(
      async (event: ChangeEvent<HTMLInputElement>) => {
        const isEnabled = event.currentTarget.checked;
        const { id, type, active, isManuallyTriggered } = accountIntegration;
        try {
          await accountIntegrationsStore.updateAccountIntegration<
            TAutomatedReferralsConfig
          >(id, {
            type,
            active,
            isManuallyTriggered,
            integrationConfig: {
              referralSystemId: integrationConfig.referralSystemId,
              sendOutOfAreaLeads: isEnabled,
            },
          });
          toast({
            ...DEFAULT_SUCCESS_TOAST_OPTIONS,
            description: (
              <ApiMessageStack
                messageStack={
                  "Automated Referrals integration configuration successfully updated."
                }
              />
            ),
          });
        } catch (error) {
          toast({
            ...DEFAULT_ERROR_TOAST_OPTIONS,
            description: <ApiMessageStack messageStack={error.message} />,
          });
        }
      },
      [accountIntegration, accountIntegrationsStore, integrationConfig, toast]
    );

    return (
      <Box mb={6}>
        <Box fontSize={"xl"} color={"gray.700"} mb={4}>
          Out of area leads
        </Box>
        <Grid templateColumns="75% 25%" rowGap={4}>
          <VStack alignItems={"left"}>
            <Box color={"gray.700"}>Send out of area leads</Box>
            <Box fontSize={"sm"} maxWidth={"580px"} color={"leadpro.400"}>
              When enabled, leads outside your offices' areas are automatically
              sent to Members Hub as referrals. Turn this off if you only want
              to receive referrals.
            </Box>
          </VStack>
          <Flex justifyContent={"center"} alignItems={"flex-start"} pt={1}>
            <Switch
              size={"sm"}
              colorScheme={"blue"}
              isChecked={sendOutOfAreaLeads}
              onChange={handleSendOutOfAreaLeadsChange}
            />
          </Flex>
        </Grid>
      </Box>
    );
  }
);
